import type { ClientErrorReport } from '@pm/shared';
import { config } from '@/config';
import { createReporter, pmContext } from './client-errors';

/**
 * The browser half of PM diagnostics: failures the server can never see,
 * reported on PM pages only. The rules (which pages, how many, how often) are
 * in client-errors.ts; this file is the wiring to the window.
 */

/**
 * Plain fetch, not the app's api client: a 401 while reporting a crash must
 * not be taken for the session ending, and a report that fails to send must
 * not be reported in turn. keepalive lets it outlive the navigation a crash
 * often sets off.
 */
const report = createReporter({
  send: (body) => {
    void fetch(`${config.apiBaseUrl}/pm/client-errors`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => undefined);
  },
});

/** Errors already reported as network failures, so they are not reported again as rejections. */
const reported = new WeakSet<object>();

const messageOf = (error: unknown) =>
  error instanceof Error ? error.message : typeof error === 'string' ? error : String(error ?? '');
const stackOf = (error: unknown) => (error instanceof Error ? error.stack : undefined);

function reportFromPage(failure: Pick<ClientErrorReport, 'kind' | 'message'> & Partial<ClientErrorReport>) {
  if (typeof window === 'undefined') return;
  const { isPm, boardId, workspaceId } = pmContext(window.location.pathname);
  if (!isPm) return;
  report({
    ...failure,
    url: `${window.location.pathname}${window.location.search}`,
    boardId,
    workspaceId,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  });
}

/** A component threw while rendering; the page's error boundary caught it. */
export function reportRenderError(error: unknown, componentStack?: string | null) {
  reportFromPage({
    kind: 'render_error',
    message: messageOf(error),
    stack: stackOf(error),
    // The innermost component is the first frame of React's component stack.
    component: /at (\S+)/.exec(componentStack ?? '')?.[1],
  });
}

/** A request that never got an answer - offline, DNS, a dropped connection. */
export function reportNetworkFailure(method: string, path: string, error: unknown) {
  // The page cancelled it; nothing failed.
  if ((error as { name?: string } | null)?.name === 'AbortError') return;
  if (error && typeof error === 'object') reported.add(error);
  reportFromPage({
    kind: 'network_error',
    message: messageOf(error) || 'Request failed with no response',
    requestMethod: method,
    requestUrl: path,
  });
}

let installed = false;

/** Listens for the failures nothing else catches. Called once, before the app renders. */
export function installCrashCapture() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason;
    // An API error came back with a response, so the request log has it.
    if (reason instanceof Error && reason.name === 'ApiError') return;
    if (reason && typeof reason === 'object' && reported.has(reason)) return;
    reportFromPage({ kind: 'unhandled_rejection', message: messageOf(reason), stack: stackOf(reason) });
  });

  window.addEventListener('error', (event) => {
    // A failed image or script load arrives here with no error object: noise, not a bug.
    if (!event.error) return;
    reportFromPage({
      kind: 'window_error', message: messageOf(event.error) || event.message, stack: stackOf(event.error),
    });
  });
}
